// Step fiche display — one structured card per main pipeline step (MexaScreen chat).
const FICHE_TITLES_EN = {
    project: 'Project card',
    target: 'Target card',
    pdb: 'PDB Selection',
    binding_site: 'Binding site card',
    peptide_candidates: 'Peptide card',
    screening: 'Step 5 · Screening',
    docking: 'Docking Launch',
};
const FICHE_TITLES_FR = {
    project: 'Fiche projet',
    target: 'Fiche target',
    pdb: 'Sélection PDB',
    binding_site: 'Fiche site de liaison',
    peptide_candidates: 'Fiche peptide',
    screening: 'Étape 5 · Screening',
    docking: 'Lancement docking',
};
function uiLang(data) {
    return (data === null || data === void 0 ? void 0 : data.ui_lang) === 'fr' ? 'fr' : 'en';
}
function ficheTitle(type, lang = 'en') {
    const table = lang === 'fr' ? FICHE_TITLES_FR : FICHE_TITLES_EN;
    return table[type] || (lang === 'fr' ? 'Récapitulatif' : 'Step summary');
}
const PDB_ROW_LABELS = new Set([
    'Selected PDB',
    'PDB sélectionné',
    'Resolution',
    'Method',
    'Ligand',
    'Note',
    'Status',
]);
const TARGET_ROW_LABELS = new Set([
    'Target',
    'Cible',
    'UniProt',
    'Mechanism',
    'Mécanisme',
    'Status',
    'Statut',
]);
export function shouldShowStepFiche(phase) {
    return phase === 'brief_complete' || phase === 'between_steps';
}
const FICHE_PLACEHOLDER_VALUES = new Set([
    '',
    'unknown',
    'n/a',
    'na',
    'none',
    'null',
    '—',
    '-',
]);
function formatFicheValue(value) {
    const trimmed = (value !== null && value !== void 0 ? value : '').trim();
    if (!trimmed || FICHE_PLACEHOLDER_VALUES.has(trimmed.toLowerCase())) {
        return '—';
    }
    return trimmed;
}
function row(label, value) {
    return { label, value: formatFicheValue(value) };
}
function statusRow(complete) {
    return {
        label: 'Status',
        value: complete ? 'Complete' : 'Incomplete',
        kind: 'status',
        status: complete ? 'complete' : 'incomplete',
    };
}
function joinList(items) {
    if (!(items === null || items === void 0 ? void 0 : items.length))
        return '—';
    return items.join(', ');
}
function buildProjectFiche(brief, lang = 'en') {
    var _a;
    const confirmed = new Set((_a = brief.confirmed_fields) !== null && _a !== void 0 ? _a : []);
    const rows = [
        row('Target', brief.target),
        row('Pathology', brief.pathology),
        row('Strategy', brief.strategy),
        row('Constraints', joinList(brief.constraints)),
    ];
    rows.push(statusRow(confirmed.size >= 4));
    return {
        fiche_type: 'project',
        title: ficheTitle('project', lang),
        rows,
    };
}
function buildTargetFiche(fiche, lang = 'en') {
    return {
        fiche_type: 'target',
        title: ficheTitle('target', lang),
        rows: [
            row('Target', fiche.target),
            row('UniProt', fiche.uniprot_id),
            row('Mechanism', fiche.mechanism),
        ],
    };
}
function buildPdbFiche(data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const snapPdb = (_a = data.state_snapshot) === null || _a === void 0 ? void 0 : _a.pdb_fiche;
    const pdb = {
        pdb_id: (_d = (_c = (_b = data.pdb_fiche) === null || _b === void 0 ? void 0 : _b.pdb_id) !== null && _c !== void 0 ? _c : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.pdb_id) !== null && _d !== void 0 ? _d : data.selected_pdb,
        resolution: (_f = (_e = data.pdb_fiche) === null || _e === void 0 ? void 0 : _e.resolution) !== null && _f !== void 0 ? _f : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.resolution,
        method: (_h = (_g = data.pdb_fiche) === null || _g === void 0 ? void 0 : _g.method) !== null && _h !== void 0 ? _h : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.method,
        ligand: (_k = (_j = data.pdb_fiche) === null || _j === void 0 ? void 0 : _j.ligand) !== null && _k !== void 0 ? _k : snapPdb === null || snapPdb === void 0 ? void 0 : snapPdb.ligand,
    };
    const rows = [row('Selected PDB', pdb.pdb_id)];
    if ((_l = pdb.resolution) === null || _l === void 0 ? void 0 : _l.trim())
        rows.push(row('Resolution', pdb.resolution));
    if ((_m = pdb.method) === null || _m === void 0 ? void 0 : _m.trim())
        rows.push(row('Method', pdb.method));
    if ((_o = pdb.ligand) === null || _o === void 0 ? void 0 : _o.trim())
        rows.push(row('Ligand', pdb.ligand));
    return {
        fiche_type: 'pdb',
        title: ficheTitle('pdb', uiLang(data)),
        rows,
    };
}
function filterRowsForFicheType(ficheType, rows) {
    return rows.filter((r) => {
        if (r.kind === 'status')
            return true;
        if (ficheType === 'pdb') {
            return PDB_ROW_LABELS.has(r.label);
        }
        if (ficheType === 'target') {
            return TARGET_ROW_LABELS.has(r.label);
        }
        return true;
    });
}
/** Strip cross-step leakage (e.g. target mutations on PDB fiche from legacy agent payloads). */
function sanitizeIncomingStepFiche(fiche, data) {
    const normalized = Object.assign(Object.assign({}, fiche), { 
        // Always use canonical titles (overrides legacy "Récap peptide", etc.).
        title: ficheTitle(fiche.fiche_type, uiLang(data)) || fiche.title || 'Step summary', rows: fiche.rows.map(normalizeStepFicheRow) });
    if (normalized.fiche_type === 'pdb') {
        const filtered = filterRowsForFicheType('pdb', normalized.rows);
        const hasPdb = filtered.some((r) => (r.label === 'Selected PDB' || r.label === 'PDB sélectionné') && r.value !== '—');
        if (!hasPdb || filtered.length <= 1) {
            return buildPdbFiche(data);
        }
        return Object.assign(Object.assign({}, normalized), { rows: filtered });
    }
    if (normalized.fiche_type === 'target') {
        return Object.assign(Object.assign({}, normalized), { rows: filterRowsForFicheType('target', normalized.rows) });
    }
    return normalized;
}
function buildBindingSiteFiche(site, selectedPdb, lang = 'en') {
    var _a, _b;
    const centroid = ((_a = site.centroid) === null || _a === void 0 ? void 0 : _a.length) ? `[${site.centroid.join(', ')}]` : '—';
    return {
        fiche_type: 'binding_site',
        title: ficheTitle('binding_site', lang),
        rows: [
            row('PDB', (_b = site.pdb_id) !== null && _b !== void 0 ? _b : selectedPdb),
            row('Pocket ID', site.pocket_id),
            row('Score', site.score != null ? String(site.score) : '—'),
            row('Residues', joinList(site.residues)),
            row('Centroid', centroid),
        ],
    };
}
function buildPeptideCandidatesFiche(candidates, selectedPdb, lang = 'en') {
    const rows = [
        row('Selected PDB', selectedPdb),
        row('Candidates', candidates.length ? String(candidates.length) : '0'),
    ];
    candidates.slice(0, 8).forEach((c) => {
        var _a, _b, _c;
        rows.push(row((_a = c.id) !== null && _a !== void 0 ? _a : 'Candidate', `${(_b = c.sequence) !== null && _b !== void 0 ? _b : '—'} · score ${(_c = c.docking_score) !== null && _c !== void 0 ? _c : '—'}`));
    });
    if (candidates.length > 8) {
        rows.push(row('…', `${candidates.length - 8} more`));
    }
    return {
        fiche_type: 'peptide_candidates',
        title: ficheTitle('peptide_candidates', lang),
        rows,
    };
}
/** Prefer unified step_fiche from LangGraph; legacy fields as fallback until agent is updated. */
export function resolveStepFiche(data) {
    var _a, _b, _c, _d, _e, _f, _g;
    const phase = (_a = data.phase) !== null && _a !== void 0 ? _a : 'collecting_brief';
    if (!shouldShowStepFiche(phase))
        return null;
    const snap = data.state_snapshot;
    if ((snap === null || snap === void 0 ? void 0 : snap.suppress_step_fiche) || (snap === null || snap === void 0 ? void 0 : snap.keep_action_banner)) {
        return null;
    }
    if ((_c = (_b = data.step_fiche) === null || _b === void 0 ? void 0 : _b.rows) === null || _c === void 0 ? void 0 : _c.length) {
        return sanitizeIncomingStepFiche(data.step_fiche, data);
    }
    // Ops check/launch cards this turn → never invent a Peptide Fiche from candidates.
    // Backend sends step_fiche=null on purpose; legacy fallback would re-stack cards.
    const notices = data.chat_notices;
    if (Array.isArray(notices) && notices.length > 0) {
        return null;
    }
    const step = (_d = data.current_step) !== null && _d !== void 0 ? _d : '';
    const lang = uiLang(data);
    if (phase === 'brief_complete' && data.project_brief) {
        return buildProjectFiche(data.project_brief, lang);
    }
    if (phase === 'between_steps') {
        // Target Card only when LangGraph sent step_fiche (enter step / explicit ask).
        if (step === 'pdb_selection' && (data.selected_pdb || ((_e = data.pdb_fiche) === null || _e === void 0 ? void 0 : _e.pdb_id))) {
            return buildPdbFiche(data);
        }
        if (step === 'receptor_preparation' && !((_f = data.state_snapshot) === null || _f === void 0 ? void 0 : _f.receptor_id)) {
            return buildPdbFiche(data);
        }
        if (step === 'binding_site' && data.binding_site) {
            return buildBindingSiteFiche(data.binding_site, data.selected_pdb, lang);
        }
        if (step === 'peptide_design' && ((_g = data.candidates) === null || _g === void 0 ? void 0 : _g.length)) {
            return buildPeptideCandidatesFiche(data.candidates, data.selected_pdb, lang);
        }
    }
    return null;
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function parseStatusValue(value) {
    const v = value.trim().toLowerCase();
    if (v === 'complete' || v === 'completed' || v === 'done' || v === 'ok') {
        return 'complete';
    }
    if (v === 'incomplete' || v === 'pending' || v === 'draft' || v === 'in_progress') {
        return 'incomplete';
    }
    // Freeform CTA / instructional text must not become an Incomplete badge.
    return null;
}
function normalizeStepFicheRow(r) {
    var _a;
    if (r.kind === 'status' || r.label.toLowerCase() === 'status') {
        const status = (_a = r.status) !== null && _a !== void 0 ? _a : parseStatusValue(r.value);
        if (!status) {
            // Keep as a normal text row (e.g. Action-like copy wrongly labelled Status).
            return Object.assign(Object.assign({}, r), { kind: undefined, status: undefined });
        }
        return Object.assign(Object.assign({}, r), { kind: 'status', status, value: status === 'complete' ? 'Complete' : 'Incomplete' });
    }
    return r;
}
function renderStatusBadge(status) {
    const isComplete = status === 'complete';
    const label = isComplete ? 'Complete' : 'Incomplete';
    const icon = isComplete ? '✓' : '!';
    return `<span class="chat-step-fiche-status-badge chat-step-fiche-status-${status}" role="status" aria-label="Status: ${label}">
    <span class="chat-step-fiche-status-icon" aria-hidden="true">${icon}</span>
    ${escapeHtml(label)}
  </span>`;
}
function renderFicheRow(r, ficheType) {
    const normalized = normalizeStepFicheRow(r);
    if (normalized.kind === 'status') {
        return '';
    }
    if (ficheType === 'pdb' && normalized.label === 'Note') {
        return `<div class="chat-step-fiche-note">${escapeHtml(normalized.value)}</div>`;
    }
    const isSequence = ficheType === 'peptide_candidates' &&
        /^sequence|s[eé]quence$/i.test(normalized.label);
    const rowClass = isSequence
        ? 'chat-step-fiche-row chat-step-fiche-row-sequence'
        : 'chat-step-fiche-row';
    return `<div class="${rowClass}">
    <span>${escapeHtml(normalized.label)}</span>
    <strong>${escapeHtml(normalized.value)}</strong>
  </div>`;
}
function renderStatusFooter(rows) {
    var _a;
    const statusRow = rows.map(normalizeStepFicheRow).find((r) => r.kind === 'status');
    if (!statusRow)
        return '';
    const status = (_a = statusRow.status) !== null && _a !== void 0 ? _a : parseStatusValue(statusRow.value);
    if (!status)
        return '';
    const badge = renderStatusBadge(status);
    return `<div class="chat-step-fiche-status-footer">${badge}</div>`;
}
export function appendStepFiche(fiche) {
    const chatHistory = document.getElementById('chat-history');
    if (!chatHistory)
        return;
    // Refresh in place only when the previous card of this type is still at the
    // bottom of the thread (aside from ephemeral banners). If the user already
    // sent new messages after it, keep that card in history and append a new one.
    const prev = chatHistory.querySelector(`.chat-step-fiche-${fiche.fiche_type}:last-of-type`);
    if (prev && !hasNewerChatContentAfter(prev)) {
        prev.remove();
    }
    const rowsHtml = fiche.rows.map((r) => renderFicheRow(r, fiche.fiche_type)).join('');
    const statusFooter = renderStatusFooter(fiche.rows);
    const block = document.createElement('div');
    block.className = `chat-step-fiche chat-step-fiche-${fiche.fiche_type}`;
    block.dataset.ficheType = fiche.fiche_type;
    block.innerHTML = `
    <div class="chat-step-fiche-title">${escapeHtml(fiche.title)}</div>
    <div class="chat-step-fiche-body">${rowsHtml}</div>
    ${statusFooter}
  `;
    chatHistory.appendChild(block);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
function hasNewerChatContentAfter(el) {
    let node = el.nextSibling;
    while (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const next = node;
            const ephemeral = next.id === 'langgraph-action-banner' ||
                next.id === 'langgraph-confirm-actions' ||
                next.classList.contains('typing');
            if (!ephemeral)
                return true;
        }
        node = node.nextSibling;
    }
    return false;
}
export function stepFicheDedupKey(data, fiche) {
    var _a, _b;
    const contentKey = fiche.rows
        .map((r) => { var _a; return `${r.label}:${r.value}:${(_a = r.status) !== null && _a !== void 0 ? _a : ''}`; })
        .join('|');
    return `${(_a = data.project_id) !== null && _a !== void 0 ? _a : ''}:${(_b = data.current_step) !== null && _b !== void 0 ? _b : ''}:${fiche.fiche_type}:${contentKey}`;
}
